import { API } from '../../api';
import { Collapse, Divider, Input, PageHeader, Row, Spin, Table, notification } from 'antd';
import { DimaContext } from '../../DimaContext';
import { Link, useParams, useRouteMatch } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';

const { Panel } = Collapse;
const { TextArea } = Input;

const JobDetail = () => {
  const context = useContext(DimaContext);
  const { jobId } = useParams();
  const [job, setJob] = useState({});
  const [jobActions, setJobActions] = useState([]);
  const [childJobs, setChildJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const { url } = useRouteMatch();

  const jobStatusToColorMap = {
    Failed: {
      color: '#cf1322',
      border: '#ffa39e',
      background: '#fff1f0',
    },
    Processing: {
      color: '#d46b08',
      border: '#ffd591',
      background: 'fff7e6',
    },
    Partial: {
      color: '#096dd9',
      border: '#91d5ff',
      background: '#e6f7ff',
    },
    Succeeded: {
      color: '#389e0d',
      border: '#b7eb8f',
      background: '#f6ffed',
    },
  };

  const actionStatusToColorMap = {
    Failed: {
      color: '#cf1322',
      border: '#ffa39e',
      background: '#fff1f0',
    },
    Processing: {
      color: '#d46b08',
      border: '#ffd591',
      background: 'fff7e6',
    },
    Queued: {
      color: '#096dd9',
      border: '#91d5ff',
      background: '#e6f7ff',
    },
    Succeeded: {
      color: '#389e0d',
      border: '#b7eb8f',
      background: '#f6ffed',
    },
    Created: {
      color: '#000000',
      border: '#000000',
      background: '#FFFFFF',
    },
  };

  const fetchJobById = async () => {
    setLoading(true);

    try {
      const { data } = await API.get(`/dis/jobs/${jobId}?clientId=${context.client.id}`);
      setJob(data);
    } catch (err) {
      notification.error({
        message: 'Error',
        description: 'Failed to get job',
        duration: 3,
        placement: 'topLeft',
      });
    }
  };

  const fetchChildJobsByParentJobId = async () => {
    try {
      const { data } = await API.get(`/dis/jobs/${jobId}/children?clientId=${context.client.id}`);
      setChildJobs(data.results);
    } catch (err) {
      notification.error({
        message: 'Error',
        description: `Failed to get child jobs for job ${jobId}`,
        duration: 3,
        placement: 'topLeft',
      });
    }

    setLoading(false);
  };

  const fetchActionsByJobId = async () => {
    try {
      const { data } = await API.get(`/dis/jobs/${jobId}/actions?clientId=${context.client.id}`);
      setJobActions(data.results);
    } catch (err) {
      notification.error({
        message: 'Error',
        description: `Failed to get actions for job ${jobId}`,
        duration: 3,
        placement: 'topLeft',
      });
    }
  };

  const generateJobActionsColumns = () => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: id => {
          const linkUrl = `${url}/action/${id}`;
          return (
            <Link to={linkUrl} data-cy={`action-link-${id}`}>
              {id}
            </Link>
          );
        },
      },
      {
        title: 'Action Type',
        dataIndex: 'actionType',
        key: 'actionType',
        render: (actionType, action) => {
          const urlBase = url.slice(0, url.indexOf('/job'));
          const linkUrl = `${urlBase}/actionType/${action?.actionType}`;
          return (
            <Link to={linkUrl} data-cy={`actiontype-link-${actionType}`}>
              {actionType}
            </Link>
          );
        },
      },
      {
        title: 'Started',
        dataIndex: 'startedAt',
        key: 'startedAt',
        sorter: (a, b) => {
          if (a.startedAt === null || b.startedAt === null) return 1;
          return new Date(a.startedAt) - new Date(b.startedAt);
        },
        render: startedAt => {
          return <span>{startedAt ? new Date(startedAt).toUTCString() : 'N/A'}</span>;
        },
      },
      {
        title: 'Completed',
        dataIndex: 'finishedAt',
        key: 'finishedAt',
        sorter: (a, b) => {
          if (a.finishedAt === null || b.finishedAt === null) return 1;
          return new Date(a.finishedAt) - new Date(b.finishedAt);
        },
        defaultSortOrder: 'descend',
        render: finishedAt => {
          return <span>{finishedAt ? new Date(finishedAt).toUTCString() : 'N/A'}</span>;
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        sorter: (a, b) => {
          if (a.status === null || b.status === null) return 1;
          return a.status.localeCompare(b.status);
        },
        render: status => {
          return (
            <span
              style={{
                border: '1px solid',
                borderColor: actionStatusToColorMap[status].border,
                padding: '5px 10px',
                backgroundColor: actionStatusToColorMap[status].background,
                color: actionStatusToColorMap[status].color,
              }}
            >
              {' '}
              {status}{' '}
            </span>
          );
        },
      },
    ];
  };

  const generateChildJobColumns = () => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: id => {
          const urlBase = url.slice(0, url.indexOf('/job'));
          const linkUrl = `${urlBase}/job/${id}`;
          return <Link to={linkUrl}>{id}</Link>;
        },
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: (a, b) => {
          if (a.created_at === null || b.created_at === null) return 1;
          return new Date(a.created_at) - new Date(b.created_at);
        },
        render: createdAt => {
          return <span>{new Date(createdAt).toUTCString()}</span>;
        },
      },
      {
        title: 'Updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        sorter: (a, b) => {
          if (a.updated_at === null || b.updated_at === null) return 1;
          return new Date(a.updated_at) - new Date(b.updated_at);
        },
        defaultSortOrder: 'descend',
        render: updatedAt => {
          return <span>{new Date(updatedAt).toUTCString()}</span>;
        },
      },
      {
        title: 'Job Type',
        dataIndex: 'jobType',
        key: 'jobType',
        render: jobType => {
          const urlBase = url.slice(0, url.indexOf('/job'));
          const linkUrl = `${urlBase}/jobType/${jobType.name}`;
          return (
            <Link to={linkUrl} data-cy={`childJob-jobType-link-${jobType.name}`}>
              {jobType.name}
            </Link>
          );
        },
      },
      {
        title: 'Routed With',
        dataIndex: 'routedWith',
        key: 'routedWith',
        render: routedWith => {
          if (!routedWith) return null;
          return (
            <div>
              <pre>{JSON.stringify(routedWith, null, 2).replace(/[{}"]/g, '')}</pre>
            </div>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        sorter: (a, b) => {
          if (a.status === null || b.status === null) return 1;
          return a.status.localeCompare(b.status);
        },
        render: status => {
          return (
            <span
              style={{
                border: '1px solid',
                borderColor: jobStatusToColorMap[status].border,
                padding: '5px 10px',
                backgroundColor: jobStatusToColorMap[status].background,
                color: jobStatusToColorMap[status].color,
              }}
            >
              {' '}
              {status}{' '}
            </span>
          );
        },
      },
    ];
  };

  const parentJobLink = id => {
    const urlBase = url.slice(0, Math.max(0, url.indexOf('/job')));
    const linkUrl = `${urlBase}/job/${id}`;
    return (
      <p>
        <span style={{ fontWeight: 'bold' }}>Parent Job: </span>
        <Link to={linkUrl}>{id}</Link>
      </p>
    );
  };

  const jobTypeLink = jobType => {
    const urlBase = url.slice(0, Math.max(0, url.indexOf('/job')));
    const linkUrl = `${urlBase}/jobType/${jobType.name}`;
    return (
      <p>
        <span style={{ fontWeight: 'bold' }}>Job Type: </span>
        <Link to={linkUrl} data-cy={`jobType-link-${jobType.name}`}>
          {jobType.name}
        </Link>
      </p>
    );
  };

  useEffect(() => {
    setChildJobs([]);
    setJob({});
    fetchJobById();
    fetchActionsByJobId();
    fetchChildJobsByParentJobId();
  }, [jobId]);

  return (
    <>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title="Job Detail"
      />
      <Row>
        <p>
          <span style={{ fontWeight: 'bold' }}>ID: </span>
          {jobId}
        </p>
      </Row>
      <Spin spinning={loading}>
        <div style={{ marginBottom: '10px' }}>
          <span style={{ color: 'black', fontWeight: 'bold' }}>Status:</span>
          <span
            style={{
              border: '1px solid',
              borderColor: jobStatusToColorMap[job?.status]?.border,
              padding: '5px 10px',
              marginLeft: '10px',
              backgroundColor: jobStatusToColorMap[job?.status]?.background,
              color: jobStatusToColorMap[job?.status]?.color,
            }}
          >
            {' '}
            {job?.status}{' '}
          </span>
        </div>
        {job.jobType && <div>{jobTypeLink(job.jobType)}</div>}
        {job.parent && <div>{parentJobLink(job.parent.id)}</div>}
        <Divider orientation="left" orientationMargin={0}>
          Actions
        </Divider>
        <Table
          dataSource={[...jobActions]}
          columns={generateJobActionsColumns()}
          pagination={false}
          loading={loading}
          showExpandColumn={false}
          rowClassName={row => (row.active === false ? 'table-row-dark ' : 'table-row-light')}
        />
        <Collapse style={{ marginTop: '10px' }}>
          <Panel header="Errors">
            <TextArea
              id="job-errors-textarea"
              data-cy="job-errors-textarea"
              bordered
              autoSize
              value={JSON.stringify(job?.errors, null, 2)}
              style={{ caretColor: 'transparent' }}
            />
          </Panel>
          <Panel header="Payload">
            <TextArea
              id="job-payload-textarea"
              data-cy="job-payload-textarea"
              bordered
              autoSize
              value={JSON.stringify(job?.payload, null, 2)}
              style={{ caretColor: 'transparent' }}
            />
          </Panel>
          <Panel header="Routing">
            <TextArea
              id="job-routing-textarea"
              data-cy="job-routing-textarea"
              bordered
              autoSize
              value={JSON.stringify(job?.routing, null, 2)}
              style={{ caretColor: 'transparent' }}
            />
          </Panel>
          <Panel header="Child Jobs">
            <Table
              dataSource={[...childJobs]}
              columns={generateChildJobColumns()}
              pagination={false}
              loading={loading}
              showExpandColumn={false}
              rowClassName={row => (row.active === false ? 'table-row-dark ' : 'table-row-light')}
            />
          </Panel>
        </Collapse>
      </Spin>
    </>
  );
};

export default JobDetail;
